import theme from 'theme/index'
import {get, isNil, isEmpty} from 'lodash'
import {isMailchimpEnabled} from 'helpers/integrations/mailchimp'
import {getColorStatusOption} from 'helpers/pivotTable'
import {getContactName, SUBSCRIPTION_TYPE} from 'helpers/contacts'

export const EmailingIntegration = {
  HUBSPOT: 'HUBSPOT',
  MAILCHIMP: 'MAILCHIMP',
}

export const getEmailingIntegrationName = (emailingIntegration) => {
  switch (emailingIntegration) {
    case EmailingIntegration.HUBSPOT:
      return 'HubSpot'
    case EmailingIntegration.MAILCHIMP:
      return 'MailChimp'
    default:
      return 'Invalid emailing integration'
  }
}

export const PostedStatus = {
  NOT_POSTED: 0,
  POSTED: 1,
  FAILED_TO_POST: 2,
}

export const getPostedStatusName = (status) => {
  switch (status) {
    case PostedStatus.NOT_POSTED:
      return 'Not Sent'
    case PostedStatus.POSTED:
      return 'Sent'
    case PostedStatus.FAILED_TO_POST:
      return 'Failed to Send'
    default:
      return null
  }
}

export const getColorForPostedStatus = (status) => {
  switch (status) {
    case PostedStatus.NOT_POSTED:
      return theme.palette.contacts.postedStatus.notPosted
    case PostedStatus.POSTED:
      return theme.palette.contacts.postedStatus.posted
    case PostedStatus.FAILED_TO_POST:
      return theme.palette.contacts.postedStatus.failedToPost
    default:
      return theme.palette.contacts.postedStatus.notPosted
  }
}

export const EmailSendStatus = {
  NOT_SENT: 0,
  OK: 1,
  SOFT_BOUNCE: 2,
  HARD_BOUNCE: 3,
}

export const getEmailSendStatusName = (status) => {
  switch (status) {
    case EmailSendStatus.NOT_SENT:
      return 'Not Sent'
    case EmailSendStatus.OK:
      return 'Sent'
    case EmailSendStatus.SOFT_BOUNCE:
      return 'Soft Bounce'
    case EmailSendStatus.HARD_BOUNCE:
      return 'Hard Bounce'
    default:
      return null
  }
}

export const getColorForEmailSendStatus = (status) => {
  switch (status) {
    case EmailSendStatus.NOT_SENT:
      return theme.palette.contacts.emailSendStatus.notSent
    case EmailSendStatus.OK:
      return theme.palette.contacts.emailSendStatus.ok
    case EmailSendStatus.SOFT_BOUNCE:
      return theme.palette.contacts.emailSendStatus.softBounce
    case EmailSendStatus.HARD_BOUNCE:
      return theme.palette.contacts.emailSendStatus.hardBounce
    default:
      return theme.palette.contacts.emailSendStatus.notSent
  }
}

export const getIsEmailValidName = (contact) => {
  const isEmailValid = get(contact, 'emailValid', false)
  return isEmailValid ? 'Valid' : 'Invalid'
}

export const getColorForIsEmailValid = (contact) => {
  const isEmailValid = get(contact, 'emailValid', false)
  return isEmailValid
      ? theme.palette.contacts.emailValidation.valid
      : theme.palette.contacts.emailValidation.invalid
}

export const populateFieldsWithEmailingFields = (user, fields) => {
  const updatedFields = {...fields}

  updatedFields.emailValid = {
    key: 'emailValid',
    label: 'Email Status',
    data: e => getColorStatusOption(getColorForIsEmailValid(e), getIsEmailValidName(e)),
    group: e => getIsEmailValidName(e),
    groupData: e => getIsEmailValidName(e),
    export: e => getIsEmailValidName(e),
    page: 'optIn',
    visible: false,
  }

  if (isMailchimpEnabled(user)) {
    updatedFields.postedStatus = {
      key: 'postedStatus',
      label: 'Contact Sent Status',
      align: 'left',
      data: e => {
        const postedStatus = get(e, 'postedStatus', null)
        return getColorStatusOption(getColorForPostedStatus(postedStatus), getPostedStatusName(postedStatus))
      },
      export: e => getPostedStatusName(get(e, 'postedStatus', null)),
      group: e => getPostedStatusName(get(e, 'postedStatus', null)),
      groupData: e => getPostedStatusName(get(e, 'postedStatus', null)),
      page: 'optIn',
      visible: false,
    }

    updatedFields.postedFailure = {
      key: 'postedFailure',
      label: 'Contact Not Sent Reason',
      data: e => get(e, 'postedFailure', null),
      page: 'optIn',
      visible: false,
    }

    updatedFields.emailSendStatus = {
      key: 'emailSendStatus',
      label: 'Email Sent Status',
      align: 'left',
      data: e => {
        const emailSendStatus = get(e, 'emailSendStatus', null)
        return getColorStatusOption(getColorForEmailSendStatus(emailSendStatus), getEmailSendStatusName(emailSendStatus))
      },
      export: e => getEmailSendStatusName(get(e, 'emailSendStatus', null)),
      group: e => getEmailSendStatusName(get(e, 'emailSendStatus', null)),
      groupData: e => getEmailSendStatusName(get(e, 'emailSendStatus', null)),
      page: 'optIn',
      visible: false,
    }
  }

  return updatedFields
}

export const MergeFieldsToRemove = [
  'company.coverReferral',
  'company.createdBy',
  'company.createdDate',
  'company.currencyId',
  'company.modifiedBy',
  'company.modifiedDate',
  'company.pricingGroupId',
  'company.productIds',
  'company.referralCompanyId',
  'company.referralType',
  'company.referralValue',
  'company.taxCodeId',
  'contact.accountManagerId',
  'contact.campaignId',
  'contact.campaignId',
  'contact.contactMethodIds',
  'contact.createdDate',
  'contact.creatorId',
  'contact.emailStatusModifierId',
  'contact.initialCampaignId',
  'contact.modifiedDate',
  'contact.modifierId',
]

export const isContactSubscribed = (contact) => {
  const emailStatus = get(contact, 'emailStatus', SUBSCRIPTION_TYPE.systemOptOut)
  return emailStatus === SUBSCRIPTION_TYPE.userOptIn ||
      emailStatus === SUBSCRIPTION_TYPE.recipientOptIn
}

export const isEmailValidAndOptedIn = (contact) => {
  const emailValid = get(contact, 'emailValid', false)
  const emailStatus = get(contact, 'emailStatus', SUBSCRIPTION_TYPE.systemOptOut)
  return emailValid &&
      (emailStatus === SUBSCRIPTION_TYPE.userOptIn || emailStatus === SUBSCRIPTION_TYPE.recipientOptIn)
}

export const getEmailingValidationErrors = (contact) => {
  const validationErrors = []

  const contactName = getContactName(contact)
  const emailValid = get(contact, 'emailValid', false)

  if (!emailValid)
    validationErrors.push(`${contactName} does not have a valid email address.`)

  if (!isContactSubscribed(contact))
    validationErrors.push(`${contactName} has not subscribed to receive email communication.`)

  return validationErrors
}

export const getDefaultFromName = (user) => {
  const name = get(user, 'name', '')
  const accountName = get(user, 'accountName', '')
  return `${name} (${accountName})`
}

export const findSendAddress = (address, sendAddresses) => {
  return sendAddresses.find(sendAddress => sendAddress.address === address) || null
}

export const getDefaultAddress = (sendAddresses) => {
  return sendAddresses.find(sendAddress => sendAddress.default === true) || null
}

export const getFromName = (previousFromName, user) => {
  return !isEmpty(previousFromName)
      ? previousFromName
      : getDefaultFromName(user)
}

export const getReplyTo = (previousReplyTo, sendAddresses) => {
  const defaultAddress = getDefaultAddress(sendAddresses)

  return !isEmpty(previousReplyTo)
      ? previousReplyTo
      : get(defaultAddress, 'address', '')
}

export const getReplyToName = (previousReplyTo, sendAddresses) => {
  if (isEmpty(previousReplyTo)) {
    const defaultAddress = getDefaultAddress(sendAddresses)
    return get(defaultAddress, 'name', '')
  }

  const sendAddress = findSendAddress(previousReplyTo, sendAddresses)
  return isNil(sendAddress)
      ? previousReplyTo
      : sendAddress.name
}
